import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import styled from 'styled-components';
import { getClients } from "../../../actions/clients";
import { Client, Profile } from "../../../types/types";
import colors from '../../../styles/colors';
import ABButton from "../../../components/ABButton";
import { ToastContainer, toast } from "react-toastify";
import { getUserEditing, updateUserEditing } from "../../../actions/user-editing";
import ABSmallButton from "../../../components/ABSmallButton";
import { getProfile, updateProfile } from "../../../actions/profile";

interface RouteParams {
  id: string;
}

interface UserDetails {
  email?: string;
  clientId?: string | null;
  role?: string;
}

const PageContainer = styled.div`
  padding: 20px 40px;
`;

const MainContentContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  padding: 20px 0;
`;

const StyledHeading = styled.div`
  padding: 10px 0;
  font-size: 40px;
`;

const StyledInput = styled.input`
  padding: 10px;
  font-size: 16px;
  width: 100%;
  margin-bottom: 20px;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledTitle = styled.div`
  padding: 0;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const FormSubHeading = styled.h3`
  font-size: 18px;
  margin-bottom: 10px;
  padding: 0;
`;

const StyledSelect = styled.select`
  font-size: 16px;
  padding: 9px 10px 10px 10px;
  width: 450px;
  border: 1px solid ${colors.lightGray};
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  div:first-child {
    padding-right: 10px;
  }
`;

const ActionButtonsDouble = styled.div`
  display: flex;
  padding: 20px 0;
  div:first-child {
    padding-right: 10px;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

interface EditUserFormProps {
  // createEmailContentStat: (emailContentStat: EmailContentStat) => void;
  success?: string | null;
  updateSuccess?: string | null;
  error?: string | null;
  getClients: () => void;
  clients: Client[];
  udpateUser: () => void;
  user: UserDetails;
  getUserEditing: (userId: string) => void;
  updateUserEditing: (userDetails: UserDetails, userId: string) => void;
  updateProfile: (profile: Profile, userId: string) => void;
  getProfile: (userId: string) => void;
  profile: Profile;
  profileUpdateSuccess: boolean;
}

const TextButton = styled.div`
    cursor: pointer;
    margin-right: 5px;
    color: ${colors.blue};
`;

const EditUser: React.FC<EditUserFormProps> = ({
  // createEmailContentStat,
  success,
  updateSuccess,
  error,
  getClients,
  clients,
  udpateUser,
  user,
  getUserEditing,
  updateUserEditing,
  getProfile,
  updateProfile,
  profile,
  profileUpdateSuccess
}) => {

  const [clientId, setClientId] = useState('');
  const [isQualified, setIsQualified] = useState<boolean | null>(null);

  const { id } = useParams<RouteParams>();

  useEffect(() => {
    getClients();
    getUserEditing(id);
  }, []);

  useEffect(() => {
    getProfile(id);
  }, [id]);

  useEffect(() => {
    if (user.clientId) {
      setClientId(user.clientId);
    }
  }, [user]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("User Client Updated Successfully");
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (profileUpdateSuccess) {
      toast.success("User Qualified Lead Updated Successfully");
    }
  }, [profileUpdateSuccess]);

  useEffect(() => {
    if (error) {
      toast.error("Error Updating User");
    }
  }, [error]);

  useEffect(() => {
    if (profile?.qualifiedLead !== undefined) {
      setIsQualified(profile.qualifiedLead);
    }
  }, [profile]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setClientId(e.target.value);
  }

  const handleSubmit = () => {

    if (!clientId) {
      toast.error("Please fill out all fields.");
      return;
    }

    const userDetails = {
      clientId: clientId
    }
    updateUserEditing(userDetails, id);
  };

  const handleProfileUpdate = (isQualified: boolean) => {
    updateProfile({ qualifiedLead: isQualified }, id);
  };

  const handleRemoveAccess = () => {
    const userDetails = {
      clientId: null
    }
    updateUserEditing(userDetails, id);
    setClientId("");
  }

  return (
    <PageContainer>
      <HeadingContainer>
        <StyledHeading>Edit User ID: {id}</StyledHeading>
      </HeadingContainer>
      <MainContentContainer>
        <Section>
          <StyledTitle>Select a client that the user can access stat info.</StyledTitle>
          <FormSubHeading>Client ID:</FormSubHeading>
          {
            (clients && clients.length > 0) &&
            <StyledSelect
              name="clientId"
              value={clientId}
              onChange={handleSelectChange}
              placeholder="Enter Client Id"
            >
              <option value="">Select</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.company} - {client.id}
                </option>
              ))}
            </StyledSelect>
          }
          <ActionButtonsDouble>
            <ABSmallButton title="Allow Access" onClick={handleSubmit} />
            <ABSmallButton title="Remove Access" onClick={handleRemoveAccess} destructive />
          </ActionButtonsDouble>
        </Section>
        <Section>
          <FormSubHeading>Is User a Qualified Lead:</FormSubHeading>
          <StyledSelect
            name="isQualified"
            value={isQualified === null ? '' : isQualified ? 'true' : 'false'}
            onChange={(e) => {
              const value = e.target.value;
              setIsQualified(value === '' ? null : value === 'true');
            }}
          >
            <option value="">Select</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </StyledSelect>
          <ActionButtons>
            <ABSmallButton
              title="Save"
              disabled={isQualified === null}
              onClick={() => handleProfileUpdate(isQualified!)}
            />
          </ActionButtons>
        </Section>
      </MainContentContainer>

      <ToastContainer />
    </PageContainer >
  );
}

const mapStateToProps = (state: any) => {
  return {
    error: state.userEditing.error,
    success: state.userEditing.success,
    updateSuccess: state.userEditing.updateSuccess,
    clients: state.clients.data,
    user: state.userEditing.data,
    profileSuccessMessage: state.profile.successMessage,
    profile: state.profile.data,
    profileUpdateSuccess: state.profile.profileUpdateSuccess
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  getClients: () => dispatch(getClients()),
  getUserEditing: (userId: string) => dispatch(getUserEditing(userId)),
  updateUserEditing: (userDetails: UserDetails, userId: string) => dispatch(updateUserEditing(userDetails, userId)),
  updateProfile: (profileInfo: Profile, userId: string) => dispatch(updateProfile(profileInfo, userId)),
  getProfile: (userId: string) => dispatch(getProfile(userId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditUser);